import React, { memo } from 'react';
import { CTASection } from '../../components/CTASection';
import { Zap, CheckCircle, Sparkles, Award, Layers, Target, DollarSign } from 'lucide-react';
export function LaserEngraving() {
  const capabilities = [{
    icon: <Sparkles className="w-6 h-6" data-id="element-1649" />,
    title: 'Unlimited Customization',
    description: 'Your ideas are limitless! We create stunning designs tailored to your specifications'
  }, {
    icon: <Layers className="w-6 h-6" data-id="element-1650" />,
    title: 'Materials Galore',
    description: 'From polycarbonate to acrylic, metal, glass, and wood—we work with a wide range of materials'
  }, {
    icon: <Target className="w-6 h-6" data-id="element-1651" />,
    title: 'Precision Technology',
    description: 'Our Seit Laser Bridge delivers breathtaking detail and accuracy'
  }, {
    icon: <Award className="w-6 h-6" data-id="element-1652" />,
    title: 'Professional Quality',
    description: 'Each piece is crafted with care and attention to detail'
  }];
  const materials = [{
    icon: '💎',
    title: 'Acrylic',
    description: 'Great for signs, awards, keychains, and personalized display pieces'
  }, {
    icon: '🔩',
    title: 'Metal',
    description: 'Etch custom logos or text onto stainless steel tumblers, tools, pens, and more'
  }, {
    icon: '🍷',
    title: 'Glass',
    description: 'Elegant etching for wine glasses, mugs, and awards'
  }, {
    icon: '🪵',
    title: 'Wood',
    description: 'Ideal for personalizing wooden plaques, cutting boards, coasters, and picture frames'
  }];
  const applications = [{
    category: 'Promotional Products',
    items: ['Pens', 'USB drives', 'Business card holders', 'Keychains']
  }, {
    category: 'Corporate Gifts',
    items: ['Leather notebooks', 'Metal flasks', 'Wooden desk organizers', 'Executive awards']
  }, {
    category: 'Event Memorabilia',
    items: ['Trophies', 'Plaques', 'Wedding signage', 'Reunion keepsakes']
  }, {
    category: 'Custom Awards',
    items: ['Achievement plaques', 'Recognition trophies', 'Acrylic awards', 'Glass awards']
  }];
  return <div className="flex flex-col w-full" data-id="element-1653">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1654">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1655">
          <div className="flex justify-center mb-6" data-id="element-1656">
            <Zap className="w-16 h-16 text-white" data-id="element-1657" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1658">
            Laser Cutting & Engraving
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1659">
            Precision meets creativity! Unleash your imagination with
            breathtaking laser cutting and engraving that brings your visions to
            life with flair.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1661">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1662">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1663">
              Unleash Your Imagination!
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1664">
              <p data-id="element-1665">
                With our Seit Laser Bridge, added in 2009, we've elevated our
                services to offer breathtaking laser cutting and engraving.
                Whether you're looking for custom awards, intricate designs, or
                unique decorative pieces, our laser technology brings your
                visions to life with precision and flair!
              </p>
              <p data-id="element-1666">
                From polycarbonate to acrylic, metal, glass, wood, and various
                plastics, we can engrave and cut a range of materials to suit
                your project needs. Perfect for awards, gifts, promotional
                items, corporate recognition, and event memorabilia—the
                possibilities are truly limitless!
              </p>
            </div>
          </div>

          {/* Work Gallery */}
          <div className="mb-16" data-id="element-1667">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1668">
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1669">
                <img src="https://cdn.magicpatterns.com/uploads/2gzAGjUPyvn3UGmdAntK8B/Laser-Engraving1.png" alt="Laser engraving example - Leather patch on hat" className="w-full h-64 object-cover" data-id="element-1670" />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1671">
                <img src="https://cdn.magicpatterns.com/uploads/oJJuNgX4GeSKzyjgfN1Pg6/Laser-Engraving2.png" alt="Laser engraving example - Personalized wood cutting boards" className="w-full h-64 object-cover" data-id="element-1672" />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1673">
                <img src="https://cdn.magicpatterns.com/uploads/wg53n5efPyczePVzQU9Peu/Laser-Engraving3.png" alt="Laser engraving example - Custom tumblers" className="w-full h-64 object-cover" data-id="element-1674" />
              </div>
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1675">
            {capabilities.map((capability, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1676">
                <div className="flex items-start gap-4" data-id="element-1677">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1678">
                    {capability.icon}
                  </div>
                  <div data-id="element-1679">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1680">
                      {capability.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1681">{capability.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Materials We Work With */}
          <div className="mb-16" data-id="element-1682">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1683">
              Materials We Work With
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1684">
              {materials.map((material, index) => <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-wh-teal transition-colors text-center" data-id="element-1685">
                  <div className="text-5xl mb-4" data-id="element-1686">{material.icon}</div>
                  <h3 className="text-xl font-bold text-wh-teal mb-3" data-id="element-1687">
                    {material.title}
                  </h3>
                  <p className="text-gray-700 text-sm" data-id="element-1688">
                    {material.description}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Applications */}
          <div className="mb-16" data-id="element-1689">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1690">
              What We Can Create
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1691">
              {applications.map((application, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1692">
                  <h3 className="text-2xl font-bold text-wh-pink mb-4" data-id="element-1693">
                    {application.category}
                  </h3>
                  <ul className="space-y-2" data-id="element-1694">
                    {application.items.map((item, itemIndex) => <li key={itemIndex} className="flex items-center gap-3" data-id="element-1695">
                        <CheckCircle className="w-5 h-5 text-wh-teal flex-shrink-0" data-id="element-1696" />
                        <span className="text-gray-700" data-id="element-1697">{item}</span>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Technology Highlight */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1698">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1699">
              <Zap className="w-16 h-16 mx-auto mb-6" data-id="element-1700" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1701">
                Seit Laser Bridge Technology
              </h2>
              <p className="text-xl text-white/90 mb-6" data-id="element-1702">
                Since 2009, our advanced Seit Laser Bridge has been delivering
                breathtaking results with precision and accuracy. This
                state-of-the-art equipment allows us to create intricate
                designs, clean cuts, and elegant engravings across a wide
                variety of materials.
              </p>
              <p className="text-lg text-white/90" data-id="element-1703">
                Whether you need custom awards, promotional products, corporate
                gifts, or event memorabilia, our laser technology ensures every
                piece is crafted with exceptional detail and quality.
              </p>
            </div>
          </div>

          {/* Pricing Note */}
          <div className="bg-gradient-to-br from-wh-yellow to-yellow-500 rounded-2xl p-8 mb-16" data-id="element-1704">
            <div className="max-w-3xl mx-auto flex items-start gap-4" data-id="element-1705">
              <DollarSign className="w-8 h-8 text-gray-900 flex-shrink-0 mt-1" data-id="element-1706" />
              <div data-id="element-1707">
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1708">
                  Note on Pricing
                </h3>
                <p className="text-gray-800 leading-relaxed" data-id="element-1709">
                  While we deliver high-quality results, please be aware that
                  the complexity of the laser cutting process may affect costs.
                  Factors like material type, design intricacy, and project size
                  all play a role. But rest assured, each piece we create is
                  crafted with care and attention to detail! Contact us for a
                  custom quote tailored to your specific project.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-1710">
            <Sparkles className="w-16 h-16 mx-auto mb-6" data-id="element-1711" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1712">
              Ready to Make a Lasting Impression?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1713">
              Contact us to explore how our laser cutting and engraving services
              can turn your ideas into reality. From custom awards to
              promotional products, we bring precision and creativity to every
              project!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1714">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1715">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1716">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1717" />
    </div>;
}